import type { AppVersionResponse, UpdateAppVersionRequest } from '@inflip/contracts/admin';
import { APP_PLATFORM_LABELS } from '@inflip/shared';
import { useCallback, useEffect, useState } from 'react';

import {
  Badge,
  BodyRow,
  Button,
  Dialog,
  ErrorBanner,
  LoadingRow,
  PageHeader,
  TableShell,
  Td,
  Th,
  TheadRow,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';

type EditForm = {
  minVersion: string;
  latestVersion: string;
  forceUpdateMessage: string;
  optionalUpdateMessage: string;
  storeUrl: string;
  qrImageUrl: string;
};

const toForm = (row: AppVersionResponse): EditForm => ({
  minVersion: row.minVersion,
  latestVersion: row.latestVersion,
  forceUpdateMessage: row.forceUpdateMessage ?? '',
  optionalUpdateMessage: row.optionalUpdateMessage ?? '',
  storeUrl: row.storeUrl ?? '',
  qrImageUrl: row.qrImageUrl ?? '',
});

/** 앱 버전 관리 — 플랫폼별 최소(강제)/최신 버전, 강제 업데이트 문구, 스토어 URL, QR */
export function AppVersionsPage() {
  const { session } = useAdminAuth();
  const [rows, setRows] = useState<AppVersionResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AppVersionResponse | null>(null);

  const load = useCallback(async () => {
    if (!session) return;
    try {
      setRows(await api.listAppVersions(session.accessToken));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!session) return null;

  return (
    <main className="mx-auto w-full max-w-5xl px-6 py-8">
      <PageHeader
        title="앱 버전 관리"
        description="최소 버전(min)을 올리면 그보다 낮은 버전의 사용자는 앱 실행 시 강제 업데이트 화면에 막혀요. 반드시 새 빌드가 스토어에 출시된 뒤에 올리세요."
      />
      {error && <ErrorBanner message={error} />}
      {!rows && !error && <LoadingRow />}

      {rows && (
        <TableShell>
          <TheadRow>
            <Th>플랫폼</Th>
            <Th>최소 버전 (강제)</Th>
            <Th>최신 버전</Th>
            <Th>강제 업데이트 메시지</Th>
            <Th>스토어 URL</Th>
            <Th>QR</Th>
            <Th>수정일</Th>
            <Th />
          </TheadRow>
          <tbody>
            {rows.map((row) => (
              <BodyRow key={row.platform}>
                <Td>
                  <Badge tone={row.platform === 'ANDROID' ? 'green' : 'blue'}>
                    {row.platform}
                  </Badge>
                </Td>
                <Td className="font-mono text-sm font-bold text-red-600">{row.minVersion}</Td>
                <Td className="font-mono text-sm font-bold text-brand">{row.latestVersion}</Td>
                <Td className="max-w-56">
                  <span className="block truncate text-muted-foreground">
                    {row.forceUpdateMessage || '—'}
                  </span>
                </Td>
                <Td className="max-w-72">
                  <span className="block truncate font-mono text-xs text-muted-foreground">
                    {row.storeUrl || '—'}
                  </span>
                </Td>
                <Td>
                  {row.qrImageUrl ? (
                    <img src={row.qrImageUrl} alt="QR" className="h-9 w-9 rounded border border-border object-contain" />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Td>
                <Td className="whitespace-nowrap text-xs text-muted-foreground">
                  {formatDateTime(row.updatedAt)}
                </Td>
                <Td>
                  <Button variant="outline" size="sm" onClick={() => setEditing(row)}>
                    수정
                  </Button>
                </Td>
              </BodyRow>
            ))}
          </tbody>
        </TableShell>
      )}

      {editing && (
        <EditDialog
          row={editing}
          token={session.accessToken}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </main>
  );
}

function EditDialog({
  row,
  token,
  onClose,
  onSaved,
}: {
  row: AppVersionResponse;
  token: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<EditForm>(() => toForm(row));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const body: UpdateAppVersionRequest = {
        minVersion: form.minVersion.trim(),
        latestVersion: form.latestVersion.trim(),
        // 빈 문자열은 "미설정" 으로 저장
        forceUpdateMessage: form.forceUpdateMessage.trim() || null,
        optionalUpdateMessage: form.optionalUpdateMessage.trim() || null,
        storeUrl: form.storeUrl.trim() || null,
        qrImageUrl: form.qrImageUrl.trim() || null,
      };
      await api.updateAppVersion(token, row.platform, body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  return (
    <Dialog label={`${row.platform} 앱 버전 설정 수정`} onClose={onClose}>
      <p className="text-lg font-bold tracking-tight">
        {APP_PLATFORM_LABELS[row.platform]} 앱 버전 설정
      </p>
      <form
        className="mt-4 max-h-[65vh] space-y-3 overflow-y-auto pr-1"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="최소 버전 (강제 업데이트 기준) *" hint="이 버전보다 낮은 사용자는 강제 업데이트 화면에 막혀요">
          <input
            className={`w-full font-mono ${inputCls}`}
            placeholder="1.0.0"
            required
            value={form.minVersion}
            onChange={(e) => setForm({ ...form, minVersion: e.target.value })}
          />
        </Field>
        <Field label="최신 버전 *" hint="스토어에 출시된 최신 버전">
          <input
            className={`w-full font-mono ${inputCls}`}
            placeholder="1.0.0"
            required
            value={form.latestVersion}
            onChange={(e) => setForm({ ...form, latestVersion: e.target.value })}
          />
        </Field>
        <Field label="강제 업데이트 메시지">
          <textarea
            className={`h-20 w-full resize-none ${inputCls}`}
            placeholder="강제 업데이트 시 표시될 메시지 (비우면 기본 문구)"
            maxLength={500}
            value={form.forceUpdateMessage}
            onChange={(e) => setForm({ ...form, forceUpdateMessage: e.target.value })}
          />
        </Field>
        <Field label="선택 업데이트 메시지 (현재 미사용)" hint="아직 앱에 노출되는 화면이 없어요 — 예비 필드">
          <textarea
            className={`h-16 w-full resize-none ${inputCls}`}
            placeholder="선택 업데이트 안내 메시지"
            maxLength={500}
            value={form.optionalUpdateMessage}
            onChange={(e) => setForm({ ...form, optionalUpdateMessage: e.target.value })}
          />
        </Field>
        <Field label="스토어 URL" hint="업데이트 버튼이 여는 주소예요. 비우면 클라이언트 기본 fallback 사용">
          <input
            className={`w-full font-mono ${inputCls}`}
            placeholder={
              row.platform === 'ANDROID'
                ? 'market://details?id=…'
                : 'https://apps.apple.com/kr/app/…'
            }
            value={form.storeUrl}
            onChange={(e) => setForm({ ...form, storeUrl: e.target.value })}
          />
        </Field>
        <Field label="앱 다운로드 QR 이미지 URL" hint="웹사이트 앱 다운로드 카드에 노출돼요. 비우면 표시되지 않아요">
          <div className="flex items-start gap-2">
            {form.qrImageUrl.trim() && (
              <img
                src={form.qrImageUrl}
                alt="QR 미리보기"
                className="h-14 w-14 shrink-0 rounded border border-border object-contain"
              />
            )}
            <input
              className={`min-w-0 flex-1 ${inputCls}`}
              placeholder="https://…/qr.png"
              value={form.qrImageUrl}
              onChange={(e) => setForm({ ...form, qrImageUrl: e.target.value })}
            />
          </div>
        </Field>

        {error && <ErrorBanner message={error} />}

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" className="flex-1" disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-muted-foreground/80">{hint}</p>}
    </div>
  );
}
